import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import VideoPartsPanel from "../components/VideoPartsPanel";
import AdPlaceholder from "../components/AdPlaceholder";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Building2, Film, Image as ImageIcon, Search } from "lucide-react";

function sceneThumbnail(video) {
  if (video.cover_image) return video.cover_image;
  if (video.thumbnail_url) return video.thumbnail_url;
  if (video.mux_playback_id) return `https://image.mux.com/${video.mux_playback_id}/thumbnail.jpg?width=480&fit_mode=smartcrop`;
  return video.scene_photos?.[0] || "";
}

export default function StudioScenes() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    api.get("/api/videos/?limit=500")
      .then(response => setVideos((response.data || []).filter(video => video.is_studio === true)))
      .catch(error => toast.error(error?.response?.data?.detail || "Could not load Studio Scenes"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return videos;
    return videos.filter(video => `${video.title || ""} ${video.studio_name_cache || ""} ${video.cast || ""}`.toLowerCase().includes(query));
  }, [videos, search]);

  const toggleExpanded = id => setExpanded(current => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const [publishing, setPublishing] = useState(new Set());

  const toggleFreePublish = async video => {
    const publish = !video.show_in_free_area;
    setPublishing(current => new Set(current).add(video.id));
    try {
      await api.put(`/api/videos/${video.id}`, { show_in_free_area: publish });
      setVideos(current => current.map(v => (v.id === video.id ? { ...v, show_in_free_area: publish } : v)));
      toast.success(publish ? "Published to free members" : "Removed from free members");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update free members visibility");
    } finally {
      setPublishing(current => {
        const next = new Set(current);
        next.delete(video.id);
        return next;
      });
    }
  };

  return (
    <AppLayout title="Studio Scenes" overline="EXISTING STUDIO-FLAGGED VIDEOS · MANUAL PARTS & CUTS">
      <div className="space-y-4">
        {/* Ad Placeholder - Studio Scenes */}
        <AdPlaceholder placement="free_members_main" className="mb-6" />

        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-800 pb-4">
          <div className="flex items-center gap-2 text-sm text-zinc-300">
            <Building2 size={17} className="text-pink-400" />
            <span>{videos.length} flagged scenes</span>
            <span className="text-zinc-600">·</span>
            <span className="text-zinc-500">{filtered.length} shown</span>
          </div>
          <label className="flex min-w-[220px] items-center gap-2 rounded border border-zinc-700 bg-zinc-950 px-3 py-2 focus-within:border-pink-400 focus-within:ring-2 focus-within:ring-pink-500/30">
            <Search size={14} className="text-zinc-500" />
            <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search scenes, studio, cast" className="w-full bg-transparent text-sm text-white outline-none placeholder:text-zinc-600" />
          </label>
        </header>

        {loading && <div className="py-10 text-center text-sm text-zinc-500">Loading Studio Scenes...</div>}
        {!loading && filtered.length === 0 && <div className="py-12 text-center text-sm text-zinc-500">No studio-flagged videos found.</div>}

        <div className="divide-y divide-zinc-800">
          {filtered.map(video => {
            const isOpen = expanded.has(video.id);
            const thumbnail = sceneThumbnail(video);
            const sourceOptions = videos.filter(option => option.id !== video.id);
            return (
              <article key={video.id} className="py-4" data-testid={`studio-scene-${video.id}`}>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="relative aspect-video w-36 shrink-0 overflow-hidden rounded border border-zinc-800 bg-zinc-950">
                    {thumbnail ? <img src={thumbnail} alt="" loading="lazy" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-zinc-600"><ImageIcon size={22} /></div>}
                    <span className="absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded bg-black/80 px-1.5 py-0.5 text-[9px] font-bold text-pink-200"><Film size={10} /> STUDIO</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="font-semibold text-white">{video.title || "Untitled scene"}</h2>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-zinc-500">
                      {video.studio_name_cache && <span>{video.studio_name_cache}</span>}
                      {video.shoot_date && <span>{video.shoot_date}</span>}
                      {video.edit_status && <span>{video.edit_status.replaceAll("_", " ")}</span>}
                      {video.mux_status && <span>Mux {video.mux_status}</span>}
                    </div>
                    <div className="mt-1 text-[10px] text-zinc-600">Scene video ID: {video.id}</div>
                  </div>
                  <button type="button" onClick={() => toggleFreePublish(video)} disabled={publishing.has(video.id)} aria-pressed={!!video.show_in_free_area} data-testid={`studio-scene-publish-${video.id}`} className={`rounded border px-3 py-2 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-pink-400 disabled:opacity-50 ${video.show_in_free_area ? "border-emerald-400/60 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25" : "border-zinc-600 text-zinc-200 hover:bg-zinc-800"}`}>
                    {video.show_in_free_area ? "Published to Free Members" : "Publish to Free Members"}
                  </button>
                  <button type="button" onClick={() => toggleExpanded(video.id)} aria-expanded={isOpen} data-testid={`studio-scene-links-${video.id}`} className="rounded border border-pink-500/50 px-3 py-2 text-xs font-medium text-pink-100 hover:bg-pink-500/15 focus:outline-none focus:ring-2 focus:ring-pink-400">
                    {isOpen ? "Close linked assets" : "Manage linked parts & cuts"}
                  </button>
                </div>
                {isOpen && (
                  <div className="mt-4 border-l-2 border-pink-500/40 pl-4">
                    <p className="mb-3 text-xs text-zinc-500">Attach or detach existing video records as parts/cuts. These actions change only the child-link records; the existing videos remain untouched.</p>
                    <VideoPartsPanel video={video} sourceVideoOptions={sourceOptions} />
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </AppLayout>
  );
}
